import type { ComponentChildren, VNode } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { AlarmIndicator } from './AlarmIndicator';
import { NotificationBell } from './NotificationBell';
import { ConnectionIndicator } from './ConnectionIndicator';
import { Logo } from './Logo';
import { BRAND } from '../brand/brand';
import { NavItem } from './NavItem';
import { ThemeToggle } from './ThemeToggle';
import { AiAssistantButton } from './ai/AiAssistantButton';
import { UserMenu } from './UserMenu';
import './AppShell.css';

interface Props {
  title?: string;
  actions?: ComponentChildren;
  children: ComponentChildren;
}

const COLLAPSED_KEY = 'hv.shell.sidebar-collapsed.v1';

/** Console-style shell: sidebar (collapsible), thin top bar, scrolling main area.
 *  The sidebar width is content-driven when expanded (fits the widest nav label
 *  plus a small margin) and fixed at 52 px when collapsed. Preference is
 *  persisted per user in localStorage. */
export function AppShell({ title, actions, children }: Props) {
  // Nav-Politik: nur "Objekte, mit denen der Techniker per Klick auf ein Item
  // startet", stehen in der Sidebar. Das sind aktuell Karte und Anlagen —
  // jeweils Einstiegspunkte in die Plant-Ebene. Alarme + Befehle + Discovery
  // + Netzwerk-Scan sind plant-lokale Sub-Nav-Punkte (siehe PlantSubNav),
  // Rollen&Rechte + Typicals gehören ins Admin-Untermenü des UserMenu oben
  // rechts. Das globale Dashboard wurde ausgehängt weil es keinen echten
  // Cockpit-Blick über 20-30k Anlagen pro Kunde geben kann; per-Plant-
  // Dashboards leben unter /plants/:id.

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(COLLAPSED_KEY) === '1';
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(COLLAPSED_KEY, collapsed ? '1' : '0');
  }, [collapsed]);

  return (
    <div class={`hv-shell${collapsed ? ' hv-shell--collapsed' : ''}`}>
      <aside class="hv-shell__sidebar">
        <div class="hv-shell__brand">
          {/* Expanded: full HVACloud wordmark from docs/branding/logo.svg —
              same SVG the Gitea README shows. Collapsed: mark only (cloud
              silhouette without text) so it still reads inside the 52 px
              icon strip. */}
          {collapsed
            ? <Logo withText={false} height={30} />
            : <Logo withText={true}  height={56} />}
        </div>

        <nav class="hv-shell__nav" aria-label="Primary">
          <NavItem href="/" icon={<IconMap />}>Karte</NavItem>
          <NavItem href="/plants" icon={<IconPlant />}>Anlagen</NavItem>
          <NavItem href="/settings/artifacts" icon={<IconArtifact />}>Artefakte &amp; OTA</NavItem>
          {/* Alarme, Befehle, Discovery, Netzwerk-Scan sind plant-scoped —
              erreichbar über die Plant-Sub-Nav in /plants/:id/*. Kein
              cross-plant Cockpit-Blick per Default, weil das bei 20-30k
              Anlagen pro Kunde nicht scrollbar wäre. Rollen & Rechte +
              Typicals sind Admin-Sachen, sitzen im UserMenu oben rechts.
              Artefakte & OTA wurde aus dem UserMenu hochgezogen — der Zugang
              wird zu häufig gebraucht um ihn hinter dem Konto-Menu zu
              verstecken. */}
        </nav>

        <div class="hv-shell__spacer" />

        {BRAND.poweredBy && (
          <div class="hv-shell__powered" title="Plattform: HVACloud von sinnograte">Technik: HVACloud</div>
        )}

        {/* Angemeldeter User + Sign out sitzen im UserMenu oben rechts —
            hier in der Sidebar wäre das redundant. */}

        <button
          type="button"
          class="hv-shell__collapse"
          onClick={() => setCollapsed((c) => !c)}
          aria-label={collapsed ? 'Sidebar ausklappen' : 'Sidebar einklappen'}
          title={collapsed ? 'Sidebar ausklappen' : 'Sidebar einklappen'}
        >
          <IconCollapse collapsed={collapsed} />
        </button>
      </aside>

      <div class="hv-shell__main">
        <header class="hv-shell__topbar">
          <h1 class="hv-shell__title">{title ?? ''}</h1>
          <div class="hv-shell__actions">
            {actions}
            <AiAssistantButton />
            <AlarmIndicator />
            <NotificationBell />
            <ConnectionIndicator />
            <ThemeToggle />
            <UserMenu />
          </div>
        </header>
        <main class="hv-shell__content">
          {children}
        </main>
      </div>
    </div>
  );
}

// --- Nav-icon set --------------------------------------------------------
// Simple 20 px monochrome glyphs. currentColor pipes the sidebar's active /
// hover state down to the strokes so no icon needs to know about theme
// tokens. Kept inline instead of an icon library to keep the bundle lean —
// six glyphs are cheaper than another dependency.

function IconBase({ children }: { children: VNode | VNode[] }) {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5"
         stroke-linecap="round" stroke-linejoin="round">
      {children}
    </svg>
  );
}

function IconMap() {
  return (
    <IconBase>
      <path d="M2.5 5.5L7 3.5l6 2 4.5-2v11L13 16.5l-6-2-4.5 2v-11z" />
      <path d="M7 3.5v11M13 5.5v11" />
    </IconBase>
  );
}

// Ehemalige Sidebar-Icons (Dashboard/Bell/Clipboard/Collection/Identity/
// Typical/SignOut) wurden entfernt: Alarme + Befehle sind jetzt Plant-Sub-
// Nav-Tabs, Collections war ein Stub ohne Feature, Rollen&Rechte + Typicals
// leben im UserMenu, SignOut sitzt ebenfalls dort, und ein globales
// Dashboard ergibt bei 20-30k Anlagen pro Kunde ohnehin keinen Sinn (per-
// Plant lebt es weiter unter /plants/:id). Wer eines der Icons wieder
// braucht, holt es aus der git-Historie.

function IconPlant() {
  // HVAC outdoor unit: rectangular housing with a fan-cross grille inside.
  // Reads as "heat pump / Anlage" rather than "potted plant", which is what
  // an operator expects when browsing to /plants.
  return (
    <IconBase>
      <rect x="3" y="4" width="14" height="12" rx="1.4" />
      <circle cx="10" cy="10" r="3.4" />
      <path d="M10 6.6v6.8M6.6 10h6.8" />
    </IconBase>
  );
}

function IconArtifact() {
  // Package + arrow-down: reads as "downloadable artefact / update package".
  // Mirrors the glyph the UserMenu used before the entry moved out of the
  // account panel into the primary sidebar.
  return (
    <IconBase>
      <path d="M10 3l6 3v8l-6 3-6-3V6l6-3z" />
      <path d="M4 6l6 3 6-3M10 9v9" />
    </IconBase>
  );
}

function IconCollapse({ collapsed }: { collapsed: boolean }) {
  // Chevron flips direction on state, matches Windows Explorer / macOS Finder
  // sidebar conventions. Placed in a small button pinned to the bottom-right
  // of the sidebar.
  return (
    <IconBase>
      {collapsed
        ? <path d="M7 4l6 6-6 6" />
        : <path d="M13 4L7 10l6 6" />}
    </IconBase>
  );
}
